"use client"

import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/Button"
import { CheckboxField } from "../ui/CheckboxField"
import { TextAreaField } from "../ui/TextAreaField"
import { TextField } from "../ui/TextField"

const ContactClientSchema = z
    .object({
        name: z.string().trim().min(2, "Please enter your name.").max(120),
        email: z.string().trim().email("Please enter a valid email.").max(254),
        subject: z.string().trim().min(2, "Please enter a subject.").max(160),
        message: z.string().trim().min(10, "Please enter a message.").max(4000),
        consent: z.boolean().refine((v) => v === true, {
            message: "Consent is required.",
        }),
        honeypot: z.string().optional(),
        timingToken: z.string().uuid(),
    })
    .strict()

type ContactValues = z.infer<typeof ContactClientSchema>

function newTimingToken() {
    return globalThis.crypto?.randomUUID?.() ?? "00000000-0000-0000-0000-000000000000"
}

export function ContactSection() {
    const [serverError, setServerError] = useState<string | null>(null)
    const [serverOk, setServerOk] = useState<boolean>(false)

    const timingToken = useMemo(() => newTimingToken(), [])

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
    } = useForm<ContactValues>({
        resolver: zodResolver(ContactClientSchema),
        defaultValues: {
            name: "",
            email: "",
            subject: "",
            message: "",
            consent: false,
            honeypot: "",
            timingToken,
        },
        mode: "onBlur",
    })

    useEffect(() => {
        setValue("timingToken", timingToken, { shouldValidate: false })
    }, [setValue, timingToken])

    async function onSubmit(values: ContactValues) {
        setServerError(null)
        setServerOk(false)

        const res = await fetch("/api/contact", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(values),
        })

        const json: unknown = await res.json().catch(() => null)

        if (!res.ok) {
            const safe =
                typeof json === "object" &&
                    json &&
                    "error" in json &&
                    typeof (json as { error?: unknown }).error === "string"
                    ? (json as { error: string }).error
                    : "Something went wrong. Please try again later."
            setServerError(safe)
            return
        }

        setServerOk(true)

        const nextToken = newTimingToken()
        reset({
            name: "",
            email: "",
            subject: "",
            message: "",
            consent: false,
            honeypot: "",
            timingToken: nextToken,
        })
        setValue("timingToken", nextToken, { shouldValidate: false })
    }

    return (
        <section id="contact" className="bg-page">
            <div className="bam-container px-6 py-24">
                <div className="mx-auto max-w-230">
                    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h2 className="text-4xl font-semibold tracking-tight text-text">
                                Contact
                            </h2>
                            <p className="mt-4 max-w-[62ch] text-base leading-relaxed text-muted">
                                For inquiries, please use the form below.
                            </p>
                        </div>
                    </header>

                    {/* Enterprise card (clean, restrained) */}
                    <div className="mt-12 rounded-[20px] bg-white shadow-(--shadow-lg)">
                        {/* subtle top tint — extremely restrained */}
                        <div
                            aria-hidden="true"
                            className="h-1 w-full rounded-t-[20px] bg-[linear-gradient(90deg,color-mix(in_oklab,var(--color-accent),transparent_35%),color-mix(in_oklab,var(--color-accent-2),transparent_55%))]"
                        />

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-8 sm:p-10" noValidate>
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                className="hidden"
                                aria-hidden="true"
                                {...register("honeypot")}
                            />
                            <input type="hidden" {...register("timingToken")} />

                            <div className="grid gap-5 sm:grid-cols-2">
                                <TextField
                                    placeholder="Name"
                                    ariaLabel="Name"
                                    autoComplete="name"
                                    error={errors.name?.message}
                                    {...register("name")}
                                />
                                <TextField
                                    placeholder="Email"
                                    ariaLabel="Email"
                                    type="email"
                                    autoComplete="email"
                                    error={errors.email?.message}
                                    {...register("email")}
                                />
                            </div>

                            <TextField
                                placeholder="Subject"
                                ariaLabel="Subject"
                                error={errors.subject?.message}
                                {...register("subject")}
                            />

                            <TextAreaField
                                placeholder="Message"
                                ariaLabel="Message"
                                rows={7}
                                error={errors.message?.message}
                                {...register("message")}
                            />

                            <CheckboxField
                                label={
                                    <>
                                        I agree that my personal data will be processed according to the{" "}
                                        <a
                                            href="/privacy"
                                            className="font-medium text-text underline decoration-[color-mix(in_oklab,var(--color-text),transparent_70%)] underline-offset-4 hover:decoration-accent"
                                        >
                                            privacy policy
                                        </a>
                                        .
                                    </>
                                }
                                error={errors.consent?.message}
                                {...register("consent")}
                            />

                            {(serverError || serverOk) ? (
                                <div
                                    className={[
                                        "rounded-2xl px-4 py-3 text-sm shadow-(--shadow-sm)",
                                        serverOk
                                            ? "bg-[color-mix(in_oklab,var(--color-accent),transparent_90%)] text-muted"
                                            : "bg-[color-mix(in_oklab,var(--color-accent-2),transparent_90%)] text-text",
                                    ].join(" ")}
                                    role={serverOk ? "status" : "alert"}
                                    aria-live={serverOk ? "polite" : "assertive"}
                                >
                                    {serverOk ? "Message sent successfully." : serverError}
                                </div>
                            ) : null}

                            <div className="pt-1">
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Sending..." : "Send message"}
                                </Button>
                            </div>

                            <p className="text-xs text-muted">
                                Please do not include sensitive data.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    )
}